import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';

import { useGuest } from '../GuestProvider';
import { useLanguage } from '../LanguageContext';
import Daisy from '../assets/images/Daisy.webp';
import Tulip from '../assets/images/Tulip.webp';
import LcorParkingDetails from '../components/LcorParkingDetails';
import OasiaParkingDetails from '../components/OasiaParkingDetails';
import ArrowIcon from '../icons/ArrowIcon';
import CalendarIcon from '../icons/CalendarIcon';
import ClockIcon from '../icons/ClockIcon';
import CompassIcon from '../icons/CompassIcon';
import DressIcon from '../icons/DressIcon';
import LocationIcon from '../icons/LocationIcon';

export const locations = [
  {
    id: 'church',
    name: 'locations.church.name',
    address: 'locations.church.address',
    byCar: 'locations.church.by_car',
    ParkingComponent: LcorParkingDetails,
    byTransport: {
      mrt: 'locations.church.mrt',
      bus: 'locations.church.bus',
    },
    filterByGuestDetails: (guestDetails) => guestDetails.is_morning,
    mapAddress:
      'https://www.google.com/maps?ll=1.321517,103.810779&z=15&t=m&hl=en-US&gl=US&mapclient=embed&cid=10045902235666635926',
  },
  {
    id: 'sentosa',
    name: 'locations.sentosa.name',
    address: 'locations.sentosa.address',
    byCar: 'locations.sentosa.by_car',
    ParkingComponent: OasiaParkingDetails,
    byTransport: {
      mrt: 'locations.sentosa.mrt',
      bus: 'locations.sentosa.bus',
    },
    filterByGuestDetails: (guestDetails) =>
      guestDetails.is_tea_ceremony || guestDetails.is_banquet,
    headerNote: (
      <p className="text-lg text-content-c2">
        Please refer to the QR code sent to you for{' '}
        <span className="font-bold">free</span> entry into Sentosa
      </p>
    ),
    mapAddress:
      'https://www.google.com/maps?ll=1.253196,103.819492&z=15&t=m&hl=en-US&gl=US&mapclient=embed&cid=5133881622006515580',
  },
];

export const schedule = [
  {
    id: 'matrimony',
    title: 'schedules.matrimony.title',
    time: 'schedules.matrimony.time',
    start: '20261017T095000',
    end: '20261017T133000',
    venue: ['schedules.matrimony.venue_1', 'schedules.matrimony.venue_2'],
    timeline: [
      { time: '09:50AM', event: 'schedules.matrimony.event_door_open' },
      { time: '10:20AM', event: 'schedules.matrimony.event_seated' },
      { time: '10:30AM', event: 'schedules.matrimony.event_matrimony' },
      { time: '11:30AM', event: 'schedules.matrimony.event_lunch' },
    ],
    descriptionIcon: <DressIcon className="h-6 w-6" />,
    descriptionTitleKey: 'dress_code',
    description: 'schedules.matrimony.description',
    mapQueryId: 'church',
    placeId: 'ChIJHzL-_AUa2jERllw2nds2aos',
    filterByGuestDetails: (guestDetails) => guestDetails.is_morning,
    endingImage: Tulip,
    sequence: 0,
  },
  {
    id: 'tea_ceremony',
    title: 'schedules.tea_ceremony.title',
    time: 'schedules.tea_ceremony.time',
    start: '20261017T174500',
    end: '20261017T184500',
    venue: ['schedules.tea_ceremony.venue_1', 'schedules.tea_ceremony.venue_2'],
    description: 'schedules.tea_ceremony.description',
    descriptionTitleKey: 'note',
    mapQueryId: 'sentosa',
    placeId: 'ChIJsfuwV2Yb2jERfMeafiI2P0c',
    filterByGuestDetails: (guestDetails) => guestDetails.is_tea_ceremony,
    endingImage: Daisy,
    sequence: 1,
  },
  {
    id: 'dinner',
    title: 'schedules.dinner.title',
    time: 'schedules.dinner.time',
    start: '20261017T183000',
    end: '20261017T223000',
    venue: ['schedules.dinner.venue_1', 'schedules.dinner.venue_2'],
    timeline: [
      {
        time: '06:30PM',
        event: 'schedules.dinner.event_cocktail',
        subtitle: 'schedules.dinner.event_cocktail_subtitle',
      },
      { time: '07:00PM', event: 'schedules.dinner.event_door_open' },
      { time: '07:20PM', event: 'schedules.dinner.event_seated' },
      { time: '07:30PM', event: 'schedules.dinner.event_dinner_banquet' },
    ],
    descriptionIcon: <DressIcon className="h-6 w-6" />,
    descriptionTitleKey: 'dress_code',
    description: 'schedules.dinner.description',
    mapQueryId: 'sentosa',
    placeId: 'ChIJsfuwV2Yb2jERfMeafiI2P0c',
    filterByGuestDetails: (guestDetails) => guestDetails.is_banquet,
    sequence: 1,
  },
];

const getCurrentDateTime = () => {
  const now = new Date();

  const yyyy = now.getFullYear();
  const MM = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  const HH = String(now.getHours()).padStart(2, '0');
  const mm = String(now.getMinutes()).padStart(2, '0');
  const ss = String(now.getSeconds()).padStart(2, '0');

  return `${yyyy}${MM}${dd}T${HH}${mm}${ss}`;
};

const downloadIcs = (event, t) => {
  const tzid = 'Asia/Singapore';
  const icsLines = [
    'BEGIN:VCALENDAR',
    'PRODID:-//Daniel and Tracer Wedding//EN',
    'VERSION:2.0',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${event.id}-wedding-event@danielandtracer.com`,
    `DTSTART;TZID=${tzid}:${event.start}`,
    `DTEND;TZID=${tzid}:${event.end}`,
    `SUMMARY:${t(event.title)}`,
    `LOCATION:${event.venue
      .map((v) => t(v))
      .join(' | ')
      .replace(/,/g, '\\,')}`,
    `DESCRIPTION:${t(event.title)}`,
    `DTSTAMP:${getCurrentDateTime()}`,
    `SEQUENCE:${event.sequence}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ];

  const blob = new Blob([icsLines.join('\r\n')], {
    type: 'text/calendar;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${event.id}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

function GettingThereDetails({ location }) {
  const { t } = useLanguage();
  const tBlock = (k) => t(`schedule.${k}`);
  const ParkingComponent = location.ParkingComponent;

  return (
    <div className="bg-white/40 backdrop-blur-sm rounded-xl p-4 border border-wedding-divider text-left space-y-4 mt-10 mb-4 shadow-sm max-w-full overflow-hidden">
      <h3 className="text-xl text-content-h2 font-semibold flex items-center gap-2">
        <CompassIcon className="w-5 h-5 inline-block" />
        {tBlock('getting_here')}
      </h3>

      {location.headerNote}

      <div className="space-y-2">
        <h4 className="text-xl text-content-h2 font-medium border-b border-wedding-divider/50 pb-1">
          {tBlock('by_car')}
        </h4>
        {ParkingComponent && <ParkingComponent />}
      </div>

      <div className="space-y-2 pt-2">
        <h4 className="text-xl text-content-h2 font-medium border-b border-wedding-divider/50 pb-1">
          {tBlock('by_public_transport')}
        </h4>
        <div className="space-y-1 text-lg text-content-c2">
          <div>
            <span className="font-semibold">{tBlock('nearest_mrt')}</span>{' '}
            {tBlock(location.byTransport.mrt)}
          </div>
          <div>
            <span className="font-semibold">{tBlock('nearest_bus')}</span>{' '}
            {tBlock(location.byTransport.bus)}
          </div>
        </div>
      </div>
    </div>
  );
}

function EventBlock({ event }) {
  const { t } = useLanguage();
  const tBlock = (k) => t(`schedule.${k}`);
  const mapSrc = `https://www.google.com/maps/embed/v1/place?q=place_id:${event.placeId}&key=AIzaSyDQqgf4lWCbpU45xTjdmDrcFX0di2fWMqw`;

  const translatedVenue = event.venue.map((v) => tBlock(v));
  const eventDescriptions = tBlock(event.description)
    .replace(/\\n/g, '\n')
    .split('\n');
  const location = locations.find((l) => l.id === event.mapQueryId);

  return (
    <div className="w-full max-w-full space-y-6">
      <h2 className="text-2xl text-content-h1 mb-2">{tBlock(event.title)}</h2>
      <p className="text-xl text-content-c1-sb-num">{tBlock(event.time)}</p>
      <div>
        {translatedVenue.map((v, i) => {
          return (
            <p key={i} className="text-xl text-content-c1-sb">
              {v}
            </p>
          );
        })}
      </div>

      <div className="flex justify-center space-x-2">
        <button
          onClick={() => downloadIcs(event, tBlock)}
          className="btn-wedding-primary"
        >
          <CalendarIcon className="w-4 h-4" />
          <p>{tBlock('add_to_calendar')}</p>
        </button>
      </div>

      <div className="divider-wedding"></div>

      {event.timeline && (
        <div className="space-y-4 flex flex-col items-center">
          <div className="flex flex-row space-x-2 items-center justify-center text-lg text-content-h2 italic">
            <ClockIcon className="w-5 h-5" />
            <p>{tBlock('timeline_title')}</p>
          </div>
          <table>
            <tbody>
              {event.timeline.map((line, i) => (
                <tr key={i}>
                  <td className="text-table-text-num-b align-top text-lg text-right pr-4">
                    {line.time}
                  </td>
                  <td className="text-table-text align-top text-left">
                    <p className="text-lg">{tBlock(line.event)}</p>
                    {line.subtitle && (
                      <p className="text-lg italic tracking-tight">
                        {tBlock(line.subtitle)}
                      </p>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {event.descriptionIcon ? (
        <div className="space-y-4">
          <div className="flex flex-row items-center justify-center space-x-2 text-lg text-content-h2 italic">
            {event.descriptionIcon}
            <p>{tBlock(event.descriptionTitleKey)}</p>
          </div>
          <div className="space-y-2">
            {eventDescriptions.map((d, i) => {
              return (
                <p key={i} className="text-lg text-content-c2 text-center">
                  {d}
                </p>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {eventDescriptions.map((d, i) => {
            return (
              <p key={i} className="text-lg text-content-c2 text-center">
                {d}
              </p>
            );
          })}
        </div>
      )}

      <div className="space-y-3 mt-8">
        <div className="flex justify-center items-center space-x-2 text-content-c2 text-base">
          <LocationIcon className="w-5 h-5" />
          <a
            href={location.mapAddress}
            target="_blank"
            rel="noopener noreferrer"
          >
            {tBlock(location.address)}
            <ArrowIcon className="w-4 h-4 inline-block" />
          </a>
        </div>

        <div className="overflow-hidden rounded-2xl border border-wedding-divider">
          <iframe
            width="100%"
            height="30%"
            className="border-none"
            loading="lazy"
            allowFullScreen
            src={mapSrc}
          />
        </div>

        {location && <GettingThereDetails location={location} />}
      </div>
    </div>
  );
}

export default function SchedulePage() {
  const { guestDetails } = useGuest();

  if (!guestDetails) {
    return null;
  }

  const filteredSchedule = schedule.filter(
    (evt) =>
      !evt.filterByGuestDetails || evt.filterByGuestDetails(guestDetails),
  );

  return (
    <div className="w-full min-h-screen flex items-center justify-center px-4">
      <div className="w-full max-w-md mx-auto space-y-5 text-center">
        {filteredSchedule.map((evt, idx) => (
          <motion.div
            key={evt.id}
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: idx * 0.12 }}
            className="w-full max-w-full flex flex-col items-center justify-center"
          >
            <EventBlock event={evt} />
            {idx < filteredSchedule.length - 1 && (
              <img
                src={evt.endingImage}
                alt="Ending"
                className="max-w-full max-h-80 p-10"
              />
            )}
          </motion.div>
        ))}
      </div>
    </div>
  );
}
